// Phase SE-7 — Result rendering for the Slip Extractor.
//
// Per SlipExtractor_Build_Plan_v1.2 §6.5 + N10 (show your work):
//   • Summary row at top — kept N/M legs, combined odds, total EV,
//     aggregate hit probability.
//   • Per kept-leg: card with the full driver list. Collapsible to
//     keep the page scannable.
//   • Per dropped-leg: muted card with the single primary reason.
//   • Action bar: "Run another extraction" + (Save-as-preset deferred).

import { useState } from "react";

import { getBrowserClient } from "../../supabase/browser.ts";

import type { FilterResult, DroppedLeg } from "../../extractor/filterExecutor.ts";
import type { ScoredLeg, SignalDriver } from "../../extractor/legScorer.ts";
import type { StressTestReport } from "../../extractor/stressTest.ts";
import {
  assertShareCardClean,
  buildShareCardModel,
  shareCardSvg,
} from "../../extractor/shareCard.ts";

// R5 — design button chrome (replaces the old sa-* button classes).
const SE_BTN_BASE: React.CSSProperties = {
  padding: "11px 22px",
  borderRadius: 2,
  fontFamily: "var(--se-font-body)",
  fontSize: 13.5,
  fontWeight: 600,
  cursor: "pointer",
};
const seBtnPrimary: React.CSSProperties = {
  ...SE_BTN_BASE,
  border: "none",
  background: "var(--se-amber)",
  color: "var(--se-surface-page)",
};
const seBtnSecondary: React.CSSProperties = {
  ...SE_BTN_BASE,
  border: "1px solid var(--se-border)",
  background: "transparent",
  color: "var(--se-text-sec)",
  fontWeight: 500,
};

/** NEL-B2 — negotiated-session surface props. Optional: when absent
 *  the ResultPanel renders exactly as the single-pass v1 flow. */
export interface NelResultProps {
  passNumber: number;
  /** C(N) = 1 − (1 − d)^N from nelSession.compoundedConfidence.
   *  N-NEL-02: this is already the DEGRADED-substrate value. */
  compoundedConfidence: number;
  /** Per the state machine — another pass is permitted. */
  canRefine: boolean;
  /** Seal should be the primary action (recommended after pass 2). */
  suggestSeal: boolean;
  /** Session already sealed — show the sealed banner, hide actions. */
  sealed: boolean;
  onRefine: () => void;
  onSeal: () => void;
  /** NEL-B4 — honest break-even statement (from
   *  computeBreakEvenAnchor). Shown ONLY on a sealed session with a
   *  declared stake. Guarantee-free by construction. */
  breakEvenStatement?: string | null;
}

export interface SlipLiability {
  leg_index: number;
  fixture: string;
  market: string | null;
  selection: string | null;
  severity: "kill" | "high" | "medium";
  code: "expired" | "high_variance_over" | "high_variance_band" | "no_conviction" | "no_value" | "weak_selection" | "longshot";
  reason: string;
}

export interface ResultPanelProps {
  result: FilterResult;
  /** Always-on liability radar — ticket-killers flagged on every score
   *  (expired/early matches, high-variance overs, no-conviction, weak). */
  liabilities?: SlipLiability[] | null;
  analysisDegraded?: boolean;
  degradedLegCount?: number;
  /** Reset back to a fresh form. */
  onReset: () => void;
  /** NEL-B2 — present only inside a negotiated session. */
  nel?: NelResultProps;
  /** T2.2 — optional, informational slip-source reputation badge.
   *  Built from sourceReputationView(); never a scoring input. */
  sourceReputation?: {
    tag: string;
    tier: "Unverified" | "Proven sharp" | "Below average";
    avgClvKept: number | null;
    totalSlips: number;
  } | null;
  /** T3.2 — adversarial stress-test report (Sika Pro). When present,
   *  the weakest-link fragility view renders above the normal
   *  kept/dropped sections. Additive/optional (contract-conformant). */
  stressReport?: StressTestReport | null;
  breakEvenStatement?: string | null;
  /** T3.1 — when present, render the "Share extraction" action. The
   *  card is built from PUBLIC fields only (N15); internals never
   *  leave the panel. Additive/optional (contract-conformant). */
  shareContext?: {
    sourceCombinedOdds: number | null;
    link?: string;
  } | null;
}

export function ResultPanel(
  {
    result,
    liabilities,
    analysisDegraded = false,
    degradedLegCount = 0,
    onReset,
    nel,
    sourceReputation,
    stressReport,
    breakEvenStatement,
    shareContext,
  }: ResultPanelProps,
): JSX.Element {
  const [handingOff, setHandingOff] = useState(false);
  const [handoffError, setHandoffError] = useState(false);

  // Slice A — hand the kept legs to the 20-agent Slip Analysis swarm. Reuses the
  // already-resolved fixtures (ingest_legs, no re-extraction / re-resolution),
  // then jumps to the analyser with the freshly-created session.
  async function deepAnalyzeWithSwarm(): Promise<void> {
    if (handingOff) return;
    setHandingOff(true);
    setHandoffError(false);
    try {
      const legs = result.kept.map((l) => ({
        home_team: l.home_team ?? null,
        away_team: l.away_team ?? null,
        league: (l as { league?: string | null }).league ?? null,
        market: l.market ?? null,
        selection: l.selection ?? null,
        odds: typeof l.odds === "number" ? l.odds : null,
        fixture_id: (l as { fixture_id?: string | null }).fixture_id ?? null,
        sport: (l as { sport?: string }).sport ?? "football",
      }));
      const { data, error } = await getBrowserClient().functions.invoke("swarm-extract", {
        body: { action: "ingest_legs", source_hint: "extractor_handoff", legs },
      });
      const sid = (data as { session_id?: string } | null)?.session_id;
      if (error || !sid) { setHandingOff(false); setHandoffError(true); return; }
      window.location.assign(`/swarm?session=${encodeURIComponent(sid)}`);
    } catch {
      setHandingOff(false);
      setHandoffError(true);
    }
  }

  // T3.1 — build + download the public share document as a premium PDF.
  // N15: only the kept legs + combined odds + trim; internals are
  // stripped at model construction and the guard re-asserts before
  // export. Browser-only work (incl. the lazy jsPDF import) lives
  // entirely inside this click handler, so the component stays SSR-safe.
  async function handleShare(): Promise<void> {
    if (typeof document === "undefined") return;
    const model = buildShareCardModel({
      keptLegs: result.kept,
      sourceCombinedOdds: shareContext?.sourceCombinedOdds ?? null,
      extractedCombinedOdds: result.combined_odds_kept,
      link: shareContext?.link,
      issuedDate: new Date().toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
    });
    assertShareCardClean(model);
    const { downloadShareCardPdf } = await import(
      "../../extractor/shareCardPdf.ts"
    );
    await downloadShareCardPdf(model);
  }
  // N09 — honest refusal: when the plan was declined, the executor
  // returns empty + declined_reason. Show the explanation.
  if (result.declined) {
    return (
      <div
        data-testid="sle-result-declined"
        style={{
          padding: "20px 18px",
          border: "1px solid var(--se-conflict-amber)",
          borderLeft: "2px solid var(--se-amber)",
          borderRadius: 4,
          background: "var(--se-surface-card)",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div
          style={{
            fontFamily: "var(--se-font-mono)",
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--se-amber)",
          }}
        >
          Can&rsquo;t honestly do that
        </div>
        <div
          style={{
            fontFamily: "var(--se-font-body)",
            fontSize: 13.5,
            color: "var(--se-text)",
            lineHeight: 1.55,
            whiteSpace: "pre-line",
          }}
        >
          {result.declined_reason ?? "Plan declined."}
        </div>
        <button
          type="button"
          onClick={onReset}
          data-testid="sle-result-declined-reset"
          style={{ ...seBtnSecondary, alignSelf: "flex-start" }}
        >
          Try again
        </button>
      </div>
    );
  }

  const kept = result.kept;
  const weakLinkRemovals = result.dropped.filter((d) => d.source_tool === "weak_link");
  const dropped = result.dropped.filter((d) => d.source_tool !== "weak_link");

  return (
    <div
      data-testid="sle-result"
      style={{ display: "flex", flexDirection: "column", gap: 14 }}
    >
      {/* Summary row */}
      <SummaryRow result={result} />

      {analysisDegraded && (
        <section
          data-testid="sle-result-degraded-analysis"
          style={{
            padding: "12px 14px",
            border: "1px solid var(--se-amber)",
            borderLeft: "2px solid var(--se-amber)",
            borderRadius: 4,
            background: "var(--se-surface-raised)",
            color: "var(--se-text-secondary)",
            fontFamily: "var(--se-font-body)",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          Analyst coverage was unavailable for {degradedLegCount || "some"} leg{degradedLegCount === 1 ? "" : "s"}. The result used the remaining verified signals and did not silently treat missing analysis as approval.
        </section>
      )}

      {breakEvenStatement && (
        <section
          data-testid="sle-result-break-even"
          style={{
            padding: "12px 14px",
            border: "1px solid var(--se-border)",
            borderRadius: 4,
            background: "var(--se-surface-raised)",
            color: "var(--se-text-sec)",
            fontFamily: "var(--se-font-body)",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          {breakEvenStatement}
        </section>
      )}

      {/* Slice A — hand the kept legs to the 20-agent Slip Analysis swarm. */}
      {result.kept.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => void deepAnalyzeWithSwarm()}
            disabled={handingOff}
            data-testid="sle-result-deep-analyze"
            style={{
              ...seBtnSecondary,
              opacity: handingOff ? 0.6 : 1,
              cursor: handingOff ? "default" : "pointer",
            }}
          >
            {handingOff ? "Sending to the swarm…" : "Deep-analyze with the 20-agent swarm →"}
          </button>
          {handoffError && (
            <span
              style={{
                fontFamily: "var(--se-font-body)",
                fontSize: 12.5,
                color: "var(--se-text-secondary, #999)",
              }}
            >
              Couldn&rsquo;t hand off — try again.
            </span>
          )}
        </div>
      )}

      {stressReport && stressReport.ranked.length > 0 && (
        <section
          data-testid="sle-result-stress"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            padding: "12px 14px",
            border: "1px solid var(--se-border)",
            borderLeft: "2px solid var(--se-amber)",
            borderRadius: 6,
            background: "var(--se-surface-raised)",
          }}
        >
          <span
            style={{
              fontFamily: "var(--se-font-mono)",
              fontSize: 11,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "var(--se-amber)",
            }}
          >
            ⚠ Stress test · weakest link
          </span>
          {stressReport.weakest && (
            <p
              data-testid="sle-result-stress-headline"
              style={{
                margin: 0,
                fontFamily: "var(--se-font-body)",
                fontSize: 13.5,
                color: "var(--se-text)",
                lineHeight: 1.55,
              }}
            >
              Most fragile: <strong>{stressReport.weakest.fixture}</strong>{" "}
              {stressReport.weakest.market} {stressReport.weakest.selection} —
              pWin {(stressReport.weakest.pWin * 100).toFixed(0)}% (±
              {(stressReport.weakest.ciWidth * 50).toFixed(0)}pp), carries{" "}
              {(stressReport.weakest.oddsShare * 100).toFixed(0)}% of the
              combined odds. This is the downside, shown plainly — your
              slip is unchanged.
            </p>
          )}
          <ul
            data-testid="sle-result-stress-list"
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: 5,
            }}
          >
            {stressReport.ranked.map((f) => (
              <li
                key={f.index}
                data-testid={`sle-result-stress-row-${f.index}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto auto",
                  gap: 10,
                  alignItems: "baseline",
                  fontFamily: "var(--se-font-mono)",
                  fontSize: 11.5,
                  color: "var(--se-text-sec)",
                }}
              >
                <span style={{ color: "var(--se-text)" }}>
                  {f.fixture} · {f.market} {f.selection}
                </span>
                <span style={{ color: "var(--se-text-muted)" }}>
                  pWin {(f.pWin * 100).toFixed(0)}%
                </span>
                <span style={{ color: "var(--se-text-muted)" }}>
                  ΔEV w/o{" "}
                  {f.evDelta === null
                    ? "—"
                    : `${f.evDelta >= 0 ? "+" : ""}${(f.evDelta * 100).toFixed(1)}%`}
                </span>
                <span
                  style={{
                    color: f.fragility > 0.5
                      ? "var(--se-drop-red)"
                      : "var(--se-text-muted)",
                  }}
                >
                  fragility {(f.fragility * 100).toFixed(0)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(result as any).target_advisory && !(result as any).target_advisory.within_tolerance && (
        <section
          data-testid="sle-result-target-advisory"
          style={{
            padding: "12px 14px",
            border: "1px solid var(--se-border)",
            borderLeft: "2px solid var(--se-amber)",
            borderRadius: 4,
            background: "var(--se-surface-raised)",
            color: "var(--se-text-sec)",
            fontFamily: "var(--se-font-body)",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          Quality limit: requested {(result as any).target_advisory.requested.toFixed(1)} odds; strongest qualifying subset reached {Number((result as any).target_advisory.achieved ?? 0).toFixed(1)}. {(result as any).target_advisory.reason}
        </section>
      )}

      {/* SE-LADDER: honest coverage advisory — no STRIKZA edge anywhere */}
      {(result as any).coverage_advisory && (
        <section
          data-testid="sle-result-coverage-advisory"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            padding: "12px 14px",
            border: "1px solid var(--se-border)",
            borderLeft: "2px solid var(--se-text-muted)",
            borderRadius: 6,
            background: "var(--se-surface-raised)",
          }}
        >
          <span
            style={{
              fontFamily: "var(--se-font-mono)",
              fontSize: 11,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "var(--se-text-muted)",
            }}
          >
            No edge anywhere
          </span>
          <p
            style={{
              margin: 0,
              fontFamily: "var(--se-font-body)",
              fontSize: 13,
              color: "var(--se-text-sec)",
              lineHeight: 1.5,
            }}
          >
            {(result as any).coverage_advisory.message}
          </p>
        </section>
      )}

      {/* Liability radar — ticket-killers flagged on EVERY score (no prune
          intent required). Filtered to legs still in the slip (kept). */}
      {(() => {
        const keptFixtures = new Set((result.kept ?? []).map((l: any) => `${l?.home_team ?? "?"} v ${l?.away_team ?? "?"}`));
        const shown = (liabilities ?? []).filter((L) => keptFixtures.has(L.fixture));
        if (shown.length === 0) return null;
        const sevColor = (s: string): string =>
          s === "kill" ? "var(--se-drop-red)" : s === "high" ? "var(--se-warn, #d98a00)" : "var(--se-text-muted)";
        const sevLabel = (s: string): string => (s === "kill" ? "DEAD" : s === "high" ? "RISKY" : "WATCH");
        return (
          <section
            data-testid="sle-result-liabilities"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              padding: "12px 14px",
              border: "1px solid var(--se-border)",
              borderLeft: "2px solid var(--se-drop-red)",
              borderRadius: 6,
              background: "var(--se-surface-raised)",
            }}
          >
            <span
              style={{
                fontFamily: "var(--se-font-mono)",
                fontSize: 11,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "var(--se-drop-red)",
              }}
            >
              ⚠ Liabilities · {shown.length} likely ticket-killer{shown.length === 1 ? "" : "s"} in your slip
            </span>
            {shown.map((L, i) => (
              <div
                key={`${L.fixture}-${L.code}-${i}`}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  paddingTop: i === 0 ? 0 : 6,
                  borderTop: i === 0 ? "none" : "1px solid var(--se-border)",
                }}
              >
                <span style={{ fontSize: 13, color: "var(--se-text)" }}>
                  <strong style={{ color: sevColor(L.severity) }}>{sevLabel(L.severity)}</strong>
                  {" · "}
                  {L.fixture}
                  {L.selection ? ` — ${L.selection}` : ""}
                </span>
                <span style={{ fontSize: 12, color: "var(--se-text-muted)" }}>{L.reason}</span>
              </div>
            ))}
          </section>
        );
      })()}

      {/* UA-2: Weak Link — legs REMOVED by quality gate (Pass 1) */}
      {weakLinkRemovals.length > 0 && (
        <section
          data-testid="sle-result-weak-links"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            padding: "12px 14px",
            border: "1px solid var(--se-border)",
            borderLeft: "2px solid var(--se-drop-red)",
            borderRadius: 6,
            background: "var(--se-surface-raised)",
          }}
        >
          <span
            style={{
              fontFamily: "var(--se-font-mono)",
              fontSize: 11,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "var(--se-drop-red)",
            }}
          >
            Weak link · {weakLinkRemovals.length} removed
          </span>
          <p
            style={{
              margin: 0,
              fontFamily: "var(--se-font-body)",
              fontSize: 13,
              color: "var(--se-text-sec)",
              lineHeight: 1.5,
            }}
          >
            These legs were most likely to kill your slip and have been removed.
            {result.weak_links && (
              <> Hit probability improved by{" "}
              <strong style={{ color: "var(--se-text)" }}>{result.weak_links.combined_improvement}</strong>.</>
            )}
          </p>
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: 5,
            }}
          >
            {weakLinkRemovals.map((wl: any, i: number) => {
              const fixture = [wl.leg?.home_team, wl.leg?.away_team].filter(Boolean).join(" v ") || `Leg ${i + 1}`;
              return (
                <li
                  key={i}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    fontFamily: "var(--se-font-mono)",
                    fontSize: 12,
                    padding: "6px 8px",
                    background: "var(--se-surface)",
                    borderRadius: 4,
                  }}
                >
                  <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
                    <span style={{ color: "var(--se-drop-red)", fontWeight: 700, minWidth: 20 }}>
                      #{i + 1}
                    </span>
                    <span style={{ color: "var(--se-text)", flex: 1 }}>
                      {fixture}
                    </span>
                    <span style={{ color: "var(--se-text-muted)" }}>
                      {wl.leg?.market} {wl.leg?.selection} @ {(wl.leg?.odds ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <span style={{ color: "var(--se-text-sec)", fontSize: 11, paddingLeft: 30 }}>
                    {wl.reason}
                  </span>
                  {wl.suggested_alternative && (
                    <span style={{
                      color: "var(--se-keep-green)",
                      fontSize: 11,
                      paddingLeft: 30,
                      fontStyle: "italic",
                    }}>
                      {(wl.suggested_alternative as any).same_fixture
                        ? "Keep the game, switch the market — "
                        : ""}
                      {wl.suggested_alternative.label}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Weak Link Warnings — weak legs kept because they're needed for the target */}
      {result.weak_link_warnings && result.weak_link_warnings.length > 0 && (
        <section
          data-testid="sle-result-weak-link-warnings"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            padding: "10px 14px",
            border: "1px solid var(--se-border)",
            borderLeft: "2px solid var(--se-amber)",
            borderRadius: 6,
            background: "var(--se-surface-raised)",
          }}
        >
          <span
            style={{
              fontFamily: "var(--se-font-mono)",
              fontSize: 11,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "var(--se-amber)",
            }}
          >
            Carried risk · {result.weak_link_warnings.length} weak legs kept
          </span>
          <p
            style={{
              margin: 0,
              fontFamily: "var(--se-font-body)",
              fontSize: 12,
              color: "var(--se-text-sec)",
              lineHeight: 1.45,
            }}
          >
            These legs are weak but needed to reach your odds target.
          </p>
          {result.weak_link_warnings.map((w: any, i: number) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 10,
                alignItems: "baseline",
                fontFamily: "var(--se-font-mono)",
                fontSize: 12,
                padding: "5px 8px",
                background: "var(--se-surface)",
                borderRadius: 4,
              }}
            >
              <span style={{ color: "var(--se-amber)", fontWeight: 600 }}>!</span>
              <span style={{ color: "var(--se-text)", flex: 1 }}>{w.fixture}</span>
              <span style={{ color: "var(--se-text-muted)" }}>
                pWin {(w.pWin * 100).toFixed(0)}%
              </span>
              <span style={{ color: "var(--se-amber)", fontSize: 11 }}>
                {w.reason}
              </span>
            </div>
          ))}
        </section>
      )}

      {/* UA-3: Correlation Shield */}
      {result.correlation_shield && result.correlation_shield.pairs.length > 0 && (
        <section
          data-testid="sle-result-correlations"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            padding: "12px 14px",
            border: "1px solid var(--se-border)",
            borderLeft: "2px solid var(--se-amber)",
            borderRadius: 6,
            background: "var(--se-surface-raised)",
          }}
        >
          <span
            style={{
              fontFamily: "var(--se-font-mono)",
              fontSize: 11,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "var(--se-amber)",
            }}
          >
            🔗 Correlation shield · {result.correlation_shield.total_pairs} pair{result.correlation_shield.total_pairs === 1 ? "" : "s"}
          </span>
          <p
            style={{
              margin: 0,
              fontFamily: "var(--se-font-body)",
              fontSize: 13,
              color: "var(--se-text-sec)",
              lineHeight: 1.5,
            }}
          >
            Your slip is approximately <strong style={{ color: "var(--se-text)" }}>{result.correlation_shield.slip_overstatement_pct}% weaker</strong> than
            the odds suggest due to correlated legs. Total correlation tax: <strong style={{ color: "var(--se-text)" }}>{result.correlation_shield.total_tax_pp}pp</strong>.
          </p>
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: 5,
            }}
          >
            {result.correlation_shield.pairs.slice(0, 5).map((pair: any, i: number) => (
              <li
                key={i}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  fontFamily: "var(--se-font-mono)",
                  fontSize: 11.5,
                  padding: "6px 8px",
                  background: "var(--se-surface)",
                  borderRadius: 4,
                }}
              >
                <span style={{ color: "var(--se-text)" }}>
                  {pair.leg_a.fixture} + {pair.leg_b.fixture}
                </span>
                <span style={{ color: "var(--se-text-muted)" }}>
                  {pair.reason} · naive {pair.naive_combined_pct}% → actual {pair.actual_combined_pct}% ({pair.tax_pp > 0 ? "-" : "+"}{Math.abs(pair.tax_pp)}pp)
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {sourceReputation && (
        <div
          data-testid="sle-result-source-rep"
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            gap: 8,
            padding: "8px 10px",
            border: "1px solid var(--se-border)",
            borderLeft: `2px solid ${
              sourceReputation.tier === "Proven sharp"
                ? "var(--se-keep-green)"
                : sourceReputation.tier === "Below average"
                  ? "var(--se-drop-red)"
                  : "var(--se-text-muted)"
            }`,
            borderRadius: 4,
            fontFamily: "var(--se-font-mono)",
            fontSize: 11,
            letterSpacing: "0.06em",
            color: "var(--se-text-sec)",
          }}
        >
          <span>
            Source <strong style={{ color: "var(--se-text)" }}>
              {sourceReputation.tag}
            </strong>
          </span>
          <span
            style={{
              color: sourceReputation.tier === "Proven sharp"
                ? "var(--se-keep-green)"
                : sourceReputation.tier === "Below average"
                  ? "var(--se-drop-red)"
                  : "var(--se-text-muted)",
            }}
          >
            {sourceReputation.tier}
          </span>
          <span style={{ color: "var(--se-text-muted)" }}>
            {sourceReputation.totalSlips < 10
              ? `track record building (${sourceReputation.totalSlips} slips)`
              : `avg CLV ${
                sourceReputation.avgClvKept === null
                  ? "—"
                  : `${sourceReputation.avgClvKept >= 0 ? "+" : ""}${(sourceReputation.avgClvKept * 100).toFixed(1)}%`
              } over ${sourceReputation.totalSlips} slips`}
            {" "}· informational, not a scoring input
          </span>
        </div>
      )}

      {nel && (
        <div
          data-testid="sle-result-nel-badge"
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            gap: 10,
            padding: "8px 10px",
            border: `1px solid ${
              nel.sealed
                ? "var(--se-keep-green)"
                : "var(--se-amber)"
            }`,
            borderRadius: 4,
            background: "rgba(0,0,0,0.02)",
            fontFamily: "var(--se-font-mono)",
            fontSize: 11,
            letterSpacing: "0.08em",
            color: "var(--se-text-sec)",
          }}
        >
          <span>
            {nel.sealed ? "SEALED · " : ""}Pass {nel.passNumber} of your
            negotiated extraction
          </span>
          <span>
            Compounded detection confidence{" "}
            <strong
              style={{
                color: nel.sealed
                  ? "var(--se-keep-green)"
                  : "var(--se-amber)",
              }}
            >
              {(nel.compoundedConfidence * 100).toFixed(2)}%
            </strong>{" "}
            (C({nel.passNumber}))
          </span>
        </div>
      )}

      {nel && nel.sealed && nel.breakEvenStatement && (
        <div
          data-testid="sle-result-nel-breakeven"
          style={{
            padding: "10px 12px",
            border: "1px solid var(--se-border)",
            borderLeft: "2px solid var(--se-keep-green)",
            borderRadius: 4,
            fontFamily: "var(--se-font-body)",
            fontSize: 12.5,
            lineHeight: 1.55,
            color: "var(--se-text-sec)",
          }}
        >
          {nel.breakEvenStatement}
        </div>
      )}

      {/* Kept legs */}
      <section data-testid="sle-result-kept" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div
          style={{
            fontFamily: "var(--se-font-mono)",
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--se-keep-green)",
          }}
        >
          ✓ Kept · {kept.length} legs
        </div>
        {kept.length === 0 ? (
          <div
            data-testid="sle-result-kept-empty"
            style={{
              fontFamily: "var(--se-font-body)",
              fontSize: 13.5,
              color: "var(--se-text-sec)",
              padding: 14,
              border: "1px dashed var(--se-border)",
              borderRadius: 4,
            }}
          >
            No legs survived the filter. Adjust the plan or start over.
          </div>
        ) : (
          kept.map((leg, i) => <KeptLegCard key={i} leg={leg} index={i} />)
        )}
      </section>

      {/* UA-5: Historical Slip DNA */}
      {(result as any).dna_profile?.ready && <SlipDnaProfile dna={(result as any).dna_profile} kept={kept} />}

      {/* UA-4: Best-Price Routing Summary */}
      <BestPriceRoutingSummary kept={kept} />

      {/* UA-1: Swap Suggestions for weak kept legs */}
      {Array.isArray(result.swap_suggestions) && result.swap_suggestions.length > 0 && (
        <section
          data-testid="sle-result-swaps"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            padding: "12px 14px",
            border: "1px solid var(--se-border)",
            borderLeft: "2px solid #3B6AB8",
            borderRadius: 6,
            background: "var(--se-surface-raised)",
          }}
        >
          <span
            style={{
              fontFamily: "var(--se-font-mono)",
              fontSize: 11,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "#6f9bff",
            }}
          >
            🔄 Stronger alternatives · {result.swap_suggestions.length} swap{result.swap_suggestions.length === 1 ? "" : "s"} found
          </span>
          <p
            style={{
              margin: 0,
              fontFamily: "var(--se-font-body)",
              fontSize: 13,
              color: "var(--se-text-sec)",
              lineHeight: 1.5,
            }}
          >
            These kept legs scored weak. The substrate has stronger picks at similar odds.
          </p>
          {result.swap_suggestions.map((sw: any, i: number) => (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr",
                gap: 10,
                alignItems: "center",
                padding: "8px 10px",
                background: "var(--se-surface)",
                borderRadius: 4,
                fontFamily: "var(--se-font-mono)",
                fontSize: 12,
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ color: "var(--se-drop-red)", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>Current</span>
                <span style={{ color: "var(--se-text)" }}>{sw.current.fixture}</span>
                <span style={{ color: "var(--se-text-muted)" }}>
                  {sw.current.market} {sw.current.selection} @{sw.current.odds?.toFixed(2)} · {(sw.current.pWin * 100).toFixed(0)}% pWin
                </span>
              </div>
              <span style={{ color: "#6f9bff", fontSize: 16, fontWeight: 700 }}>→</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ color: "var(--se-keep-green)", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>Swap</span>
                <span style={{ color: "var(--se-text)" }}>{sw.swap.fixture}</span>
                <span style={{ color: "var(--se-text-muted)" }}>
                  {sw.swap.market} {sw.swap.selection} @{sw.swap.odds?.toFixed(2)} · {(sw.swap.pWin * 100).toFixed(0)}% pWin
                </span>
                <span style={{ color: "var(--se-keep-green)", fontWeight: 600, fontSize: 11 }}>
                  +{sw.delta_pWin_pp}pp stronger
                </span>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Dropped legs */}
      {dropped.length > 0 && (
        <section
          data-testid="sle-result-dropped"
          style={{ display: "flex", flexDirection: "column", gap: 8 }}
        >
          <div
            style={{
              fontFamily: "var(--se-font-mono)",
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "var(--se-text-muted)",
            }}
          >
            Dropped · {dropped.length}
          </div>
          {dropped.map((d, i) => (
            <DroppedLegRow key={i} dropped={d} index={i} />
          ))}
        </section>
      )}

      {/* Action bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
          marginTop: 4,
        }}
      >
        {nel && !nel.sealed && nel.canRefine && (
          <button
            type="button"
            onClick={nel.onRefine}
            data-testid="sle-result-nel-refine"
            style={{
              ...seBtnSecondary,
              ...(nel.suggestSeal ? { opacity: 0.85 } : null),
            }}
          >
            Refine further
          </button>
        )}
        {nel && !nel.sealed && (
          <button
            type="button"
            onClick={nel.onSeal}
            data-testid="sle-result-nel-seal"
            style={nel.suggestSeal
              ? {
                ...seBtnPrimary,
                background: "var(--se-keep-green)",
              }
              : seBtnSecondary}
          >
            Seal this extraction
          </button>
        )}
        {shareContext && result.kept.length > 0 && (
          <button
            type="button"
            onClick={() => void handleShare()}
            data-testid="sle-result-share"
            title="Download a branded PDF of this extraction (legs + trim only)"
            style={seBtnSecondary}
          >
            Share extraction
          </button>
        )}
        <button
          type="button"
          onClick={onReset}
          data-testid="sle-result-reset"
          style={seBtnPrimary}
        >
          Run another extraction
        </button>
      </div>
    </div>
  );
}

// ─── Summary row ────────────────────────────────────────────────

function SummaryRow({ result }: { result: FilterResult }): JSX.Element {
  const odds = result.combined_odds_kept;
  const hit = result.hit_probability_kept;
  const ev = result.expected_value_kept;

  return (
    <div
      data-testid="sle-result-summary"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
        gap: 12,
        padding: "16px 18px",
        border: "1px solid var(--se-border-bright)",
        borderLeft: "2px solid var(--se-keep-green)",
        borderRadius: 4,
        background: "var(--se-surface-raised)",
      }}
    >
      <Stat label="Legs kept" value={`${result.kept.length} / ${result.kept.length + result.dropped.length}`} />
      <Stat label="Combined odds" value={odds === null ? "—" : odds.toFixed(2)} />
      <Stat
        label="Hit probability"
        value={hit === null ? "—" : `${(hit * 100).toFixed(1)}%`}
      />
      <Stat
        label="Expected value"
        value={ev === null ? "—" : `${ev >= 0 ? "+" : ""}${(ev * 100).toFixed(1)}%`}
        positive={ev !== null && ev > 0}
      />
    </div>
  );
}

function Stat({
  label,
  value,
  positive,
}: {
  label: string;
  value: string;
  positive?: boolean;
}): JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span
        style={{
          fontFamily: "var(--se-font-mono)",
          fontSize: 10,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: "var(--se-text-muted)",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: "var(--se-font-display)",
          fontSize: 22,
          color: positive ? "var(--se-keep-green)" : "var(--se-text)",
          fontWeight: 600,
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Kept leg card (with collapsible drivers) ──────────────────

function KeptLegCard({
  leg,
  index,
}: {
  leg: ScoredLeg;
  index: number;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [showContext, setShowContext] = useState(false);
  // SE-9.7 — split signal-bearing drivers (non-zero contribution)
  // from informational-only ones (0 pp: market_implied prior,
  // user_pattern_info N11 notes). Signal drivers sort by absolute
  // contribution descending so the biggest mover is first.
  const signalDrivers = leg.drivers
    .filter((d) => d.contribution_pp !== 0)
    .slice()
    .sort(
      (a, b) => Math.abs(b.contribution_pp) - Math.abs(a.contribution_pp),
    );
  const contextDrivers = leg.drivers.filter((d) => d.contribution_pp === 0);
  // T1.3 — surface a visible correlation badge (advisory, N12).
  const correlationWarning = leg.drivers.find(
    (d) => d.source === "correlation_warning",
  );
  const fixture =
    [leg.home_team, leg.away_team].filter(Boolean).join(" v ") || "—";
  const ci = leg.pWin95CI;
  const ciStr = `${(ci[0] * 100).toFixed(0)}–${(ci[1] * 100).toFixed(0)}%`;

  return (
    <div
      data-testid={`sle-result-kept-card-${index}`}
      style={{
        padding: 14,
        border: "1px solid var(--se-border)",
        borderLeft: `2px solid ${confidenceColor(leg.confidence)}`,
        borderRadius: 4,
        background: "var(--se-surface-raised)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {/* Top row: fixture + market + odds */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto auto auto",
          alignItems: "baseline",
          gap: 12,
        }}
      >
        <div
          style={{
            fontFamily: "var(--se-font-body)",
            fontSize: 14.5,
            color: "var(--se-text)",
            fontWeight: 500,
          }}
        >
          {fixture}
        </div>
        <span
          style={{
            fontFamily: "var(--se-font-mono)",
            fontSize: 11,
            letterSpacing: "0.08em",
            color: "var(--se-text-sec)",
          }}
        >
          {leg.market} · {leg.selection}
        </span>
        <span
          style={{
            fontFamily: "var(--se-font-mono)",
            fontSize: 12,
            color: "var(--se-text)",
            fontWeight: 500,
          }}
        >
          @ {(leg.odds ?? 0).toFixed(2)}
        </span>
      </div>

      {/* Metrics row */}
      <div
        style={{
          display: "flex",
          gap: 16,
          fontFamily: "var(--se-font-mono)",
          fontSize: 11,
          letterSpacing: "0.04em",
          color: "var(--se-text-sec)",
        }}
      >
        <span>
          pWin <strong style={{ color: "var(--se-text)" }}>{(leg.pWin * 100).toFixed(0)}%</strong> (95% CI {ciStr})
        </span>
        <span>
          EV{" "}
          <strong
            style={{
              color:
                leg.expectedValue > 0
                  ? "var(--se-keep-green)"
                  : "var(--se-drop-red)",
            }}
          >
            {leg.expectedValue >= 0 ? "+" : ""}
            {(leg.expectedValue * 100).toFixed(1)}%
          </strong>
        </span>
        <span data-testid={`sle-result-kept-card-${index}-confidence`}>
          Confidence{" "}
          <strong style={{ color: confidenceColor(leg.confidence) }}>
            {leg.confidence.toUpperCase()}
          </strong>
        </span>
      </div>

      {/* T1.3 — correlation advisory badge (N12: advisory, never
          exclusionary; the leg was NOT removed). */}
      {correlationWarning && (
        <div
          data-testid={`sle-result-kept-card-${index}-correlation`}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            padding: "6px 8px",
            border: "1px solid var(--se-amber)",
            borderRadius: 3,
            background: "rgba(0,0,0,0.02)",
          }}
        >
          <span
            style={{
              fontFamily: "var(--se-font-mono)",
              fontSize: 10,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--se-amber)",
            }}
          >
            ⚠ Correlated leg pair
          </span>
          <span
            style={{
              fontFamily: "var(--se-font-body)",
              fontSize: 12,
              color: "var(--se-text-sec)",
              lineHeight: 1.45,
            }}
          >
            {correlationWarning.evidence}
          </span>
        </div>
      )}

      {/* Toggle for drivers list */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`sle-result-kept-card-${index}-toggle`}
        style={{
          alignSelf: "flex-start",
          background: "transparent",
          border: "none",
          padding: 0,
          fontFamily: "var(--se-font-mono)",
          fontSize: 10.5,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--se-amber)",
          cursor: "pointer",
        }}
      >
        {open ? "Hide drivers" : `Show ${signalDrivers.length} driver${signalDrivers.length === 1 ? "" : "s"}`}
      </button>

      {open && (
        <>
          <ul
            data-testid={`sle-result-kept-card-${index}-drivers`}
            style={{
              margin: 0,
              paddingLeft: 0,
              listStyle: "none",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            {signalDrivers.length === 0 ? (
              <li
                style={{
                  fontFamily: "var(--se-font-body)",
                  fontSize: 12.5,
                  color: "var(--se-text-muted)",
                }}
              >
                No scoring signal moved this leg — priced off the market
                implied line.
              </li>
            ) : (
              signalDrivers.map((d, di) => <DriverRow key={di} driver={d} />)
            )}
          </ul>

          {contextDrivers.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setShowContext((v) => !v)}
                data-testid={`sle-result-kept-card-${index}-context-toggle`}
                style={{
                  alignSelf: "flex-start",
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  fontFamily: "var(--se-font-mono)",
                  fontSize: 10,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--se-text-muted)",
                  cursor: "pointer",
                }}
              >
                {showContext
                  ? "Hide context"
                  : `Show context (${contextDrivers.length})`}
              </button>
              {showContext && (
                <ul
                  data-testid={`sle-result-kept-card-${index}-context`}
                  style={{
                    margin: 0,
                    paddingLeft: 0,
                    listStyle: "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    opacity: 0.75,
                  }}
                >
                  {contextDrivers.map((d, di) => (
                    <DriverRow key={di} driver={d} />
                  ))}
                </ul>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function DriverRow({ driver }: { driver: SignalDriver }): JSX.Element {
  const sign =
    driver.contribution_pp > 0 ? "+" : driver.contribution_pp < 0 ? "" : "";
  const color =
    driver.contribution_pp > 0
      ? "var(--se-keep-green)"
      : driver.contribution_pp < 0
        ? "var(--se-drop-red)"
        : "var(--se-text-muted)";
  return (
    <li
      data-tool={driver.source}
      style={{
        display: "grid",
        gridTemplateColumns: "120px 60px 1fr",
        gap: 10,
        alignItems: "baseline",
        fontFamily: "var(--se-font-body)",
        fontSize: 12.5,
        color: "var(--se-text-sec)",
        lineHeight: 1.45,
      }}
    >
      <span
        style={{
          fontFamily: "var(--se-font-mono)",
          fontSize: 10.5,
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          color: "var(--se-text-muted)",
        }}
      >
        {driver.source.replace(/_/g, " ")}
      </span>
      <span
        style={{
          fontFamily: "var(--se-font-mono)",
          fontSize: 11,
          fontWeight: 600,
          color,
          textAlign: "right",
        }}
      >
        {sign}
        {driver.contribution_pp.toFixed(1)} pp
      </span>
      <span>{driver.evidence}</span>
    </li>
  );
}

function confidenceColor(c: "high" | "medium" | "low"): string {
  switch (c) {
    case "high":
      return "var(--se-keep-green)";
    case "medium":
      return "var(--se-amber)";
    case "low":
      return "var(--se-text-muted)";
  }
}

// ─── Dropped leg row (muted, single reason) ────────────────────

function DroppedLegRow({
  dropped,
  index,
}: {
  dropped: DroppedLeg;
  index: number;
}): JSX.Element {
  const fixture =
    [dropped.leg.home_team, dropped.leg.away_team].filter(Boolean).join(" v ") ||
    "—";
  return (
    <div
      data-testid={`sle-result-dropped-row-${index}`}
      data-tool={dropped.source_tool}
      style={{
        padding: "10px 14px",
        border: "1px solid var(--se-border)",
        borderRadius: 4,
        background: "var(--se-surface-page)",
        opacity: 0.75,
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 12,
        alignItems: "baseline",
        fontFamily: "var(--se-font-body)",
        fontSize: 12.5,
        color: "var(--se-text-sec)",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={{ color: "var(--se-text)" }}>
          {fixture} ·{" "}
          <span style={{ color: "var(--se-text-muted)" }}>
            {dropped.leg.market} {dropped.leg.selection} @ {(dropped.leg.odds ?? 0).toFixed(2)}
          </span>
        </span>
        <span style={{ fontSize: 12, color: "var(--se-text-sec)" }}>
          {dropped.reason}
        </span>
        {dropped.suggested_alternative && (
          <span
            data-testid={`sle-result-dropped-row-${index}-alt`}
            style={{
              fontSize: 12,
              color: "var(--se-text-sec)",
              borderLeft: "2px solid var(--se-amber)",
              paddingLeft: 8,
              marginTop: 2,
            }}
          >
            {(dropped.suggested_alternative as any).same_fixture
              ? "Keep the game, switch the market — "
              : "Sharper alternative at similar odds: "}
            <strong style={{ color: "var(--se-text)" }}>
              {dropped.suggested_alternative.label}
            </strong>{" "}
            <span style={{ color: "var(--se-text-muted)" }}>
              {(dropped.suggested_alternative as any).same_fixture
                ? "(STRIKZA's model rates this market on the same fixture — your slip is unchanged; this is a suggestion)"
                : "(Daily Acca signal — your slip is unchanged; this is just market context)"}
            </span>
          </span>
        )}
      </div>
      <span
        style={{
          fontFamily: "var(--se-font-mono)",
          fontSize: 10,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "var(--se-text-muted)",
        }}
      >
        {dropped.source_tool.replace(/_/g, " ")}
      </span>
    </div>
  );
}

// ─── UA-5: Historical Slip DNA Profile ────────────────────────────
function SlipDnaProfile({ dna, kept }: { dna: any; kept: any[] }): JSX.Element | null {
  if (!dna?.ready) return null;

  const strengths = Array.isArray(dna.strengths) ? dna.strengths : [];
  const weaknesses = Array.isArray(dna.weaknesses) ? dna.weaknesses : [];
  if (strengths.length === 0 && weaknesses.length === 0) return null;

  // Count this slip's legs that fall in weak zones
  const weakMarkets = new Set(weaknesses.map((w: any) => String(w.market).toLowerCase()));
  const weakLegsCount = kept.filter((l: any) => weakMarkets.has(String(l.market ?? "").toLowerCase())).length;

  return (
    <section
      data-testid="sle-result-dna"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: "12px 14px",
        border: "1px solid var(--se-border)",
        borderLeft: "2px solid #D4AF37",
        borderRadius: 6,
        background: "var(--se-surface-raised)",
      }}
    >
      <span
        style={{
          fontFamily: "var(--se-font-mono)",
          fontSize: 11,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: "#D4AF37",
        }}
      >
        🧬 Your slip DNA · {dna.settled_legs} settled legs
      </span>
      <p
        style={{
          margin: 0,
          fontFamily: "var(--se-font-body)",
          fontSize: 13,
          color: "var(--se-text-sec)",
          lineHeight: 1.5,
        }}
      >
        Overall hit rate: <strong style={{ color: "var(--se-text)" }}>{(dna.overall_hit_rate * 100).toFixed(1)}%</strong>.
        {weakLegsCount > 0 && (
          <> This slip has <strong style={{ color: "var(--se-drop-red)" }}>{weakLegsCount} leg{weakLegsCount === 1 ? "" : "s"}</strong> in your weak markets.</>
        )}
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {strengths.length > 0 && (
          <div>
            <div style={{ fontFamily: "var(--se-font-mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--se-keep-green)", marginBottom: 4 }}>
              ✅ Strengths
            </div>
            {strengths.slice(0, 4).map((s: any, i: number) => (
              <div key={i} style={{ fontFamily: "var(--se-font-mono)", fontSize: 11.5, color: "var(--se-text)", padding: "2px 0" }}>
                {s.market}: {(s.rate * 100).toFixed(0)}% <span style={{ color: "var(--se-keep-green)" }}>+{s.edge_pp}pp</span>
              </div>
            ))}
          </div>
        )}
        {weaknesses.length > 0 && (
          <div>
            <div style={{ fontFamily: "var(--se-font-mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--se-drop-red)", marginBottom: 4 }}>
              ⚠️ Weaknesses
            </div>
            {weaknesses.slice(0, 4).map((w: any, i: number) => (
              <div key={i} style={{ fontFamily: "var(--se-font-mono)", fontSize: 11.5, color: "var(--se-text)", padding: "2px 0" }}>
                {w.market}: {(w.rate * 100).toFixed(0)}% <span style={{ color: "var(--se-drop-red)" }}>{w.edge_pp}pp</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── UA-4: Best-Price Routing Summary ─────────────────────────────
// Aggregates per-leg best_price_available drivers into a summary
// showing how much the user would gain by shopping each leg.
function BestPriceRoutingSummary({ kept }: { kept: any[] }): JSX.Element | null {
  const routes: Array<{
    index: number; fixture: string; market: string;
    currentOdds: number; bestOdds: number; bestPlatform: string; uplift: number;
  }> = [];

  for (let i = 0; i < kept.length; i++) {
    const leg = kept[i];
    const drivers = Array.isArray(leg?.drivers) ? leg.drivers : [];
    const bpDriver = drivers.find((d: any) => d.source === "best_price_available");
    if (!bpDriver) continue;

    // Parse from evidence string: "Best return on X @ Y.YY (your primary Z.ZZ)"
    const match = String(bpDriver.evidence ?? "").match(
      /Best return on (.+?) @ (\d+\.\d+).*primary (\d+\.\d+)/,
    );
    if (!match) continue;

    const bestPlatform = match[1] ?? "";
    const bestOdds = parseFloat(match[2] ?? "0");
    const currentOdds = parseFloat(match[3] ?? "0");
    if (bestOdds <= currentOdds) continue;

    const fixture = leg.home_team && leg.away_team
      ? `${leg.home_team} vs ${leg.away_team}`
      : (leg.match_name ?? `Leg ${i + 1}`);

    routes.push({
      index: i,
      fixture,
      market: String(leg.market ?? ""),
      currentOdds,
      bestOdds,
      bestPlatform: bestPlatform,
      uplift: ((bestOdds - currentOdds) / currentOdds) * 100,
    });
  }

  if (routes.length === 0) return null;

  // Compute combined odds uplift
  let currentCombined = 1;
  let bestCombined = 1;
  for (const leg of kept) {
    const odds = typeof leg?.odds === "number" ? leg.odds : 1;
    currentCombined *= odds;
    // Check if this leg has a better price
    const route = routes.find((r) => r.index === kept.indexOf(leg));
    bestCombined *= route ? route.bestOdds : odds;
  }
  const totalUplift = currentCombined > 0
    ? ((bestCombined - currentCombined) / currentCombined) * 100
    : 0;

  const platforms = [...new Set(routes.map((r) => r.bestPlatform))];
  const top3 = routes.sort((a, b) => b.uplift - a.uplift).slice(0, 3);

  return (
    <section
      data-testid="sle-result-best-price"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: "12px 14px",
        border: "1px solid var(--se-border)",
        borderLeft: "2px solid var(--se-keep-green)",
        borderRadius: 6,
        background: "var(--se-surface-raised)",
      }}
    >
      <span
        style={{
          fontFamily: "var(--se-font-mono)",
          fontSize: 11,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: "var(--se-keep-green)",
        }}
      >
        💰 Best-price routing · {routes.length} leg{routes.length === 1 ? "" : "s"} with better prices
      </span>
      <p
        style={{
          margin: 0,
          fontFamily: "var(--se-font-body)",
          fontSize: 13,
          color: "var(--se-text-sec)",
          lineHeight: 1.5,
        }}
      >
        Switching to best available prices raises combined odds by{" "}
        <strong style={{ color: "var(--se-text)" }}>+{totalUplift.toFixed(1)}%</strong>.
        Platforms needed: <strong style={{ color: "var(--se-text)" }}>{platforms.join(", ")}</strong>.
      </p>
      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {top3.map((r, i) => (
          <li
            key={i}
            style={{
              display: "flex",
              gap: 10,
              alignItems: "baseline",
              fontFamily: "var(--se-font-mono)",
              fontSize: 11.5,
              padding: "5px 8px",
              background: "var(--se-surface)",
              borderRadius: 4,
            }}
          >
            <span style={{ color: "var(--se-text)", flex: 1 }}>
              {r.fixture}
            </span>
            <span style={{ color: "var(--se-text-muted)" }}>
              @{r.currentOdds.toFixed(2)} → @{r.bestOdds.toFixed(2)} ({r.bestPlatform})
            </span>
            <span style={{ color: "var(--se-keep-green)", fontWeight: 600 }}>
              +{r.uplift.toFixed(1)}%
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
